"use client";

import { motion } from "framer-motion";

import CustomImage from "@/components/shared/CustomImage";
import { useLanguage } from "@/contexts/LanguageContext";
import { useFancybox } from "@/hooks/useFancybox";
import type { About, Media } from "@/payload-types";
import getImageUrl from "@/utils/getImageUrl";
import type { PayLoadData } from "@/utils/payloadUtil";

export default function OurFarm({
  pageData,
}: {
  pageData: PayLoadData<About | undefined> | undefined;
}) {
  const { language } = useLanguage();

  useFancybox();

  return (
    <section id="our-farm">
      <div className="container mx-auto px-4">
        <div className="flex flex-col-reverse gap-10 md:flex-row lg:gap-16">
          {/* Box img */}
          <motion.div
            initial={{ y: -100, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="w-full flex-1 lg:w-3/5"
          >
            <div className="relative aspect-square w-full lg:aspect-video">
              {pageData?.[language]?.ourFarm?.image?.[0] && (
                <CustomImage
                  src={getImageUrl(pageData[language].ourFarm.image[0])}
                  alt={
                    (pageData[language].ourFarm.image[0] as Media).alt ||
                    "image"
                  }
                  fill
                  className="size-full object-cover pb-5 pr-5 lg:pb-10 lg:pr-20"
                />
              )}

              {pageData?.[language]?.ourFarm?.image?.[1] && (
                <div className="absolute bottom-0 right-0 aspect-square w-40 border-4 border-primary-50 lg:h-32 lg:w-60 lg:border-8 xl:h-44 xl:w-80">
                  <CustomImage
                    src={getImageUrl(pageData[language].ourFarm.image[1])}
                    alt={
                      (pageData[language].ourFarm.image[1] as Media).alt ||
                      "image"
                    }
                    fill
                    className="size-full object-cover"
                  />
                </div>
              )}
            </div>
          </motion.div>

          {/* Box content */}
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="w-full flex-1 md:w-2/5"
          >
            <div className="space-y-4">
              <h2 className="text-center text-3xl font-bold md:text-left">
                {pageData?.[language]?.ourFarm.title || ""}
              </h2>
              <p className="text-justify text-lg text-gray-600">
                {pageData?.[language]?.ourFarm.desc || ""}
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
